import React, { useCallback, useEffect, useMemo, useState } from 'react';
import feManagementSystemApi, { communityModeratorApi } from '../api/fe-management-system-api';
import { CommunityModeratorResponse } from '../api/fe-management-system-api/types';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { formatTimestampToLocaleString } from '../utils/time';

interface AdminInfoTabProps {
  tokenAddress: string;
  communityId: string;
  defaultSort?: 'time' | 'maxMc';
}

type SortType = 'time' | 'maxMc';

const AdminInfoTab: React.FC<AdminInfoTabProps> = ({ tokenAddress, communityId, defaultSort = 'time' }) => {
  const [response, setResponse] = useState<CommunityModeratorResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortType, setSortType] = useState<SortType>(defaultSort);

  const fetchAdminInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await feManagementSystemApi.getCommunityModerators(tokenAddress);
      setResponse(result);
    } catch (error: any) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [tokenAddress]);

  useEffect(() => {
    try {
      if (!communityId) {
        setError(getApiErrorMessage('No Community ID.'));
        return;
      }
      fetchAdminInfo();
    } catch (error: any) {
      setError(getApiErrorMessage(error, 'Failed to load admin info.'));
    }
  }, [tokenAddress, fetchAdminInfo, communityId]);

  const updateCommunityModerators = useCallback(async () => {
    try {
      setIsLoading(true);
      await communityModeratorApi.updateByCommunity({ communityId, tokenAddress });
      await fetchAdminInfo();
    } catch (error: any) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [communityId, tokenAddress, fetchAdminInfo]);

  const moderators = useMemo(() => response?.moderators ?? [], [response]);

  const sortedModerators = useMemo(() => {
    const rolePriority = (role: string) => {
      const normalized = role.toLowerCase();
      if (normalized === 'admin') return 0;
      if (normalized === 'moderator') return 1;
      return 2;
    };

    return [...moderators].sort((a, b) => {
      return rolePriority(a.role) - rolePriority(b.role);
    });
  }, [moderators]);

  const truncateMiddle = (value: string, head = 4, tail = 4) => {
    if (!value || value.length <= head + tail) {
      return value;
    }
    return `${value.slice(0, head)}...${value.slice(-tail)}`;
  };

  const getRoleBadgeClasses = (role: string) => {
    if (role.toLowerCase() === 'admin') {
      return 'border-green-500/70 bg-green-500/10 text-green-200';
    }

    return 'border-blue-500/70 bg-blue-500/5 text-blue-200';
  };

  const getMcTextColor = (value?: number) => {
    if (!value || value <= 0) return 'text-gray-400';
    if (value > 3_000_000) return 'text-orange-400';
    if (value > 1_000_000) return 'text-yellow-300';
    return 'text-gray-300';
  };

  const openGmgnLink = (tokenAddress?: string) => {
    if (!tokenAddress) {
      return;
    }

    const url = `https://gmgn.ai/sol/token/solscan_${tokenAddress}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const formatMaxMcValue = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return '0';
    }

    if (value >= 1_000_000) {
      return `${Math.trunc(value / 1_000_000)}M`;
    }

    if (value >= 1_000) {
      return `${Math.trunc(value / 1_000)}K`;
    }

    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-400">
        Admin 정보를 불러오는 중입니다...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-100">
        {error}
      </div>
    );
  }

  if (!moderators.length) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900/40 p-4 text-sm text-gray-300">
        현재 확인 가능한 Admin 정보가 없습니다.
      </div>
    );
  }

  const getSortedActivities = (activities: any[]) => {
    return [...activities].sort((a, b) => {
      if (sortType === 'time') {
        return new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime();
      }
      return (b.maxMc || 0) - (a.maxMc || 0);
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center px-0 pb-2">
        <div className="flex gap-2">
          <button
            onClick={() => setSortType('time')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
              sortType === 'time'
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Most Recent
          </button>
          <button
            onClick={() => setSortType('maxMc')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
              sortType === 'maxMc'
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Highest MC
          </button>
        </div>
        <button
          type="button"
          onClick={updateCommunityModerators}
          disabled={!communityId || isLoading}
          className="rounded-md border border-blue-500 bg-blue-500/20 px-3 py-1.5 text-xs font-semibold text-blue-200 transition hover:bg-blue-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Update Community Moderators
        </button>
      </div>
      <div className="overflow-hidden border border-gray-800 rounded-lg">
        <div className="max-h-[1340px] overflow-y-auto bg-gray-800/80">
          <table className="min-w-full table-fixed">
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <thead className="top-0 border-b border-gray-700 text-[11px] font-semibold uppercase text-gray-400">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-right">Followers</th>
                <th className="px-4 py-3 text-right">Following</th>
                <th className="px-4 py-3 text-right">Statuses</th>
                <th className="px-4 py-3 text-center">Role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {sortedModerators.map((moderator, index) => (
                <React.Fragment key={`${moderator.twitterId}-${index}`}>
                  <tr className="text-[14px] text-gray-200">
                    <td className="px-4 py-5 align-top">
                      <div className="flex items-center gap-2 mb-2">
                        {moderator.profile.profileImageUrl && (
                          <img
                            src={moderator.profile.profileImageUrl}
                            alt={moderator.profile.name || moderator.twitterId}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <p className="text-sm font-semibold text-white">
                          {moderator.profile.name || moderator.twitterId}
                          {moderator.profile.screenName && (
                            <p className="text-xs text-gray-400">
                              <a
                                href={`https://twitter.com/${moderator.profile.screenName}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-gray-400 hover:text-blue-200"
                              >
                                @{moderator.profile.screenName}
                              </a>
                            </p>
                          )}
                        </p>
                      </div>

                      <p className="text-xs text-gray-500 mb-1">
                        {moderator.profile.description || ''}
                      </p>
                      {moderator.profile.createdAt && (
                        <p className="text-[11px] text-gray-300">
                          Created: {formatTimestampToLocaleString(moderator.profile.createdAt)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-5 text-right align-top">
                      {moderator.profile.followersCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-5 text-right align-top">
                      {moderator.profile.followingCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-5 text-right align-top">
                      {moderator.profile.statusesCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-5 text-center align-top">
                      <span className={`inline-flex items-center justify-center rounded-full border px-3 py-0.5 text-xs font-medium ${getRoleBadgeClasses(moderator.role)}`}>
                        {moderator.role}
                      </span>
                    </td>
                  </tr>
                  {moderator.otherTokenActivities && moderator.otherTokenActivities.length > 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 pb-5 pt-0">
                        <div className="flex flex-wrap gap-3">
                          {getSortedActivities(moderator.otherTokenActivities).map((activity: any) => (
                            <div
                              key={activity.tokenAddress}
                              className="relative rounded-lg border border-gray-800/70 bg-gray-900/80 p-2 text-[14px] leading-snug w-[180px] flex-shrink-0"
                            >
                              <button
                                type="button"
                                onClick={() => openGmgnLink(activity.tokenAddress)}
                                className="absolute top-2 right-2 rounded-full border border-gray-700 bg-gray-900/60 p-1 transition-opacity hover:opacity-80"
                              >
                                <img src="/gmgn.png" alt="gmgn" className="w-3 h-3" />
                              </button>
                              <div>
                                <p className="font-semibold text-white truncate pr-5" title={activity.tokenSymbol}>
                                  {activity.tokenSymbol}
                                </p>
                                <p className="text-[11px] text-gray-400 truncate" title={activity.tokenName}>
                                  {activity.tokenName}
                                </p>
                                <p className="text-[10px] text-gray-500 font-mono">
                                  {truncateMiddle(activity.tokenAddress)}
                                </p>
                                {activity.recordedAt && (
                                  <p className="text-[9px] text-gray-400 mt-0.5">
                                    {formatTimestampToLocaleString(activity.recordedAt)}
                                  </p>
                                )}
                                <div className="mt-1 flex items-center justify-between">
                                  <span className={`text-xs font-bold ${getMcTextColor(activity.maxMc)}`}>
                                    {formatMaxMcValue(activity.maxMc)}
                                  </span>
                                  <span
                                    className={`inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-[9px] font-medium ${getRoleBadgeClasses(activity.role)}`}
                                  >
                                    {activity.role}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AdminInfoTab);
